import type { ProductCategory } from '../../api/productCategory/productCategoryApiSlice';
import { compareSortOrder } from '../../utils/manualOrder';

/**
 * The decisions behind the product-category section, separated from its JSX so
 * they can be tested (ADR-0026 §F).
 */

/** Mirrors the API's `PRODUCT_CATEGORY_KEY_PATTERN` and the CHECK constraint. */
export const CATEGORY_KEY_PATTERN = /^[a-z][a-z0-9_]{1,31}$/;

export const isValidCategoryKey = (value: string): boolean =>
    CATEGORY_KEY_PATTERN.test(value);

/**
 * A key SUGGESTED from the name — a starting point in an editable field, never
 * a value written on the user's behalf.
 *
 * The API deliberately refuses to derive the key itself, because a derived key
 * would move when the name is later corrected and configuration points at it.
 * Suggesting is a different act: the operator sees the key, can change it, and
 * confirms it once. Correcting the name afterwards cannot move it, because the
 * key is immutable after creation.
 *
 * So this is not a fourth copy of the slug rule, and it is not authoritative:
 * whatever it produces is validated by the API and by the database. It only
 * has to be good enough to usually be right.
 */
export const suggestCategoryKey = (name: string): string => {
    const base = name
        .toLowerCase()
        .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        // Single-character trims, not `_+`. After the collapse above, two
        // underscores can never be adjacent — any run of non-alphanumerics
        // became ONE underscore — so `_` and `_+` match the same thing here,
        // and the quantifier-free form has no backtracking for Sonar to
        // object to. Restoring `+` would re-raise `sonarjs/slow-regex` without
        // matching anything more.
        .replace(/^_/, '')
        .replace(/_$/, '')
        .slice(0, 32)
        .replace(/_$/, '');
    if (!base) return '';
    return /^[a-z]/.test(base) ? base : `category_${base}`.slice(0, 32);
};

export interface CategoryGroup {
    useCase: string;
    categories: ProductCategory[];
    /**
     * True when this use case is NOT among the tenant's active services.
     *
     * Such a group is still rendered. Hiding it would make the categories
     * underneath vanish from the screen while continuing to exist, to hold
     * products and to be pointed at by configuration — a disappearance that
     * reads as "there are none" rather than "this use case is switched off".
     */
    orphaned: boolean;
}

/**
 * Categories grouped under their use case, in the tenant's service order.
 *
 * Every active service gets a group even when it has no categories yet — the
 * empty group is what carries the "add one" affordance, and a use case that
 * simply did not appear would leave the operator with nowhere to start.
 */
export const groupCategoriesByUseCase = (
    categories: ProductCategory[],
    services: string[],
): CategoryGroup[] => {
    // A plain record rather than a Map: the admin-ui build targets a level
    // without downlevel iteration, so `for (… of map)` compiles under Jest's
    // Babel and fails `tsc` in the production build.
    const byUseCase: Record<string, ProductCategory[]> = {};
    for (const category of categories) {
        const list = byUseCase[category.useCase] ?? [];
        list.push(category);
        byUseCase[category.useCase] = list;
    }

    // The standard product category first (ADR-0026 §AE), then the tenant's
    // manual order (#3016), then by name — the order the home screen shows.
    const sorted = (list: ProductCategory[]) =>
        [...list].sort((a, b) =>
            Number(!!b.isStandard) - Number(!!a.isStandard)
            || compareSortOrder(a.sortOrder, b.sortOrder)
            || a.name.localeCompare(b.name));

    const groups: CategoryGroup[] = services.map((useCase) => ({
        useCase,
        categories: sorted(byUseCase[useCase] ?? []),
        orphaned: false,
    }));

    for (const useCase of Object.keys(byUseCase)) {
        if (!services.includes(useCase)) {
            groups.push({ useCase, categories: sorted(byUseCase[useCase]), orphaned: true });
        }
    }
    return groups;
};
